import { useState, useEffect, useCallback } from 'react';
import * as analyticsApi from '../../api/analytics';
import FileUploadModal from './FileUploadModal';

// BYODSourceDetail shows the active tables for a file-type data source and lets
// users upload CSV/XLSX files to create or replace tables.
//
// Upload flow (plan §3.4, two-step):
//   Step 1 — FileUploadModal: POST /data/upload → synchronous → returns {file_id}
//   Step 2 — enter table name → POST /data/byod-write → 202 → returns {workflow_id}
//   Step 3 — poll: GET /data/operations/{workflow_id} → "completed" or "failed"
function BYODSourceDetail({ dataSource }) {
  const [tables, setTables] = useState([]);
  const [fileCount, setFileCount] = useState(null);
  const [fileQuota, setFileQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Upload flow state
  const [showUploadArea, setShowUploadArea] = useState(false);   // upload area visible
  const [showUploadModal, setShowUploadModal] = useState(false); // FileUploadModal open
  const [tableName, setTableName] = useState('');
  const [uploadedFileId, setUploadedFileId] = useState(null);    // set after step 1
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [writeError, setWriteError] = useState(null);

  // Rename state: { tableName, value } or null
  // Inferred schema from the upload response (shown in step 2 before byod-write)
  const [uploadedSchema, setUploadedSchema] = useState(null); // { headers: [], types: {} }

  const [renaming, setRenaming] = useState(null);
  const [renameError, setRenameError] = useState(null);

  const loadTables = useCallback(async () => {
    if (!dataSource?.data_source_id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.listBYODTables(dataSource.data_source_id);
      // The shared fetch client resolves successful requests to response.json()
      // directly (not an Axios { data } wrapper).
      setTables(res?.tables || []);
      if (res?.file_count != null) setFileCount(res.file_count);
      if (res?.file_quota != null) setFileQuota(res.file_quota);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load tables');
    } finally {
      setLoading(false);
    }
  }, [dataSource?.data_source_id]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  // Step 1 done: FileUploadModal returned file metadata; store id + schema for step 2.
  const handleUploadComplete = useCallback((response) => {
    const data = response;
    const fileId = data?.file_id;
    if (!fileId) {
      setWriteError('Upload succeeded but no file_id was returned');
      return;
    }
    setUploadedFileId(fileId);
    if (data?.column_headers?.length) {
      setUploadedSchema({ headers: data.column_headers, types: data.column_types || {} });
    } else {
      setUploadedSchema(null);
    }
    setWriteError(null);
    setShowUploadModal(false);
    // Refresh quota counter after upload
    loadTables();
  }, [loadTables]);

  // Step 2: user has entered table_name and submitted; call byod-write + poll.
  const handleSubmitTableName = useCallback(async () => {
    if (!uploadedFileId || !tableName.trim()) return;
    setWorkflowStatus({ status: 'started' });
    setWriteError(null);

    try {
      const writeRes = await analyticsApi.byodWrite({
        file_id: uploadedFileId,
        data_source_id: dataSource.data_source_id,
        table_name: tableName.trim(),
      });
      const workflowId = writeRes?.workflow_id;
      setWorkflowStatus({ workflow_id: workflowId, status: 'started' });

      // Step 3: poll until terminal state
      setPolling(true);
      const maxAttempts = 40; // 40 × 3 s = 2 min
      let attempts = 0;
      const poll = async () => {
        try {
          const statusRes = await analyticsApi.getOperationStatus(workflowId);
          const st = statusRes?.status;
          setWorkflowStatus(statusRes);
          if (st === 'completed' || st === 'failed') {
            setPolling(false);
            if (st === 'completed') {
              setShowUploadArea(false);
              setTableName('');
              setUploadedFileId(null);
              setWorkflowStatus(null);
              loadTables();
            }
            return;
          }
          if (++attempts < maxAttempts) {
            setTimeout(poll, 3000);
          } else {
            setPolling(false);
            setWorkflowStatus((prev) => ({ ...prev, status: 'timeout' }));
          }
        } catch {
          setPolling(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (err) {
      setWriteError(err?.response?.data?.message || err?.message || 'Failed to start write workflow');
      setWorkflowStatus(null);
    }
  }, [dataSource?.data_source_id, tableName, uploadedFileId, loadTables]);

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete table "${name}"? This cannot be undone.`)) return;
    try {
      await analyticsApi.deleteBYODTable(dataSource.data_source_id, name);
      setTables((prev) => prev.filter((t) => t.table_name !== name));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete table');
    }
  };

  const handleRenameStart = (name) => {
    setRenaming({ tableName: name, value: name });
    setRenameError(null);
  };

  const handleRenameSubmit = async () => {
    if (!renaming) return;
    const newName = renaming.value.trim();
    if (!newName || newName === renaming.tableName) {
      setRenaming(null);
      return;
    }
    try {
      await analyticsApi.renameByodTable(dataSource.data_source_id, renaming.tableName, newName);
      setRenaming(null);
      setRenameError(null);
      loadTables();
    } catch (err) {
      setRenameError(err?.response?.data?.message || 'Failed to rename table');
    }
  };

  const cancelUpload = () => {
    setShowUploadArea(false);
    setTableName('');
    setUploadedFileId(null);
    setUploadedSchema(null);
    setWorkflowStatus(null);
    setWriteError(null);
    setPolling(false);
    setShowUploadModal(false);
  };

  if (!dataSource) return null;

  const quotaPct = fileCount != null && fileQuota ? Math.min(100, Math.round((fileCount / fileQuota) * 100)) : null;

  return (
    <div className="mt-4" data-testid="byod-source-detail">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold text-gray-900">Tables</h3>
        {!showUploadArea && (
          <button
            onClick={() => { setShowUploadArea(true); setShowUploadModal(true); setWriteError(null); setWorkflowStatus(null); }}
            className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
            data-testid="upload-file-btn"
          >
            Upload File
          </button>
        )}
      </div>

      {/* Upload quota bar */}
      {fileCount != null && fileQuota != null && (
        <div className="mb-4" data-testid="byod-quota-bar">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{fileCount} / {fileQuota} uploads used</span>
            {quotaPct >= 80 && (
              <span className={quotaPct >= 100 ? 'text-red-600 font-medium' : 'text-yellow-600'}>
                {quotaPct >= 100 ? 'Quota reached' : 'Approaching limit'}
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${
                quotaPct >= 100 ? 'bg-red-500' : quotaPct >= 80 ? 'bg-yellow-400' : 'bg-blue-500'
              }`}
              style={{ width: `${quotaPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Upload area: visible while flow is in progress */}
      {showUploadArea && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg" data-testid="byod-upload-form">
          <h4 className="text-sm font-medium text-gray-800 mb-3">Upload File to Table</h4>

          {writeError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="byod-write-error">
              {writeError}
            </div>
          )}

          {workflowStatus && (
            <div
              className={`mb-3 p-2 rounded text-sm border ${
                workflowStatus.status === 'completed'
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : workflowStatus.status === 'failed'
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}
              data-testid="byod-workflow-status"
            >
              {workflowStatus.status === 'started' || workflowStatus.status === 'running'
                ? 'Writing data to table...'
                : workflowStatus.status === 'completed'
                ? 'Table created successfully!'
                : workflowStatus.status === 'failed'
                ? `Failed: ${workflowStatus.error || 'Unknown error'}`
                : `Status: ${workflowStatus.status}`}
              {polling && <span className="ml-2 animate-pulse">...</span>}
            </div>
          )}

          {/* Step 1 pending: waiting for FileUploadModal to complete */}
          {!uploadedFileId && !writeError && !showUploadModal && (
            <div className="text-sm text-gray-500">Choose a file to upload...</div>
          )}

          {/* Step 2: file uploaded, now enter table name (plan §3.4 step 2) */}
          {uploadedFileId && !polling && !workflowStatus?.status?.match(/^(completed|failed|timeout)$/) && (
            <div className="space-y-3" data-testid="byod-table-name-form">
              {/* Schema preview: column names + inferred types */}
              {uploadedSchema && (
                <div data-testid="byod-schema-preview">
                  <p className="text-xs font-medium text-gray-700 mb-1">
                    Detected columns ({uploadedSchema.headers.length})
                  </p>
                  <div className="border border-gray-200 rounded overflow-hidden max-h-40 overflow-y-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-1.5 text-left font-medium text-gray-600">Column</th>
                          <th className="px-3 py-1.5 text-left font-medium text-gray-600">Type</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {uploadedSchema.headers.map((h) => (
                          <tr key={h}>
                            <td className="px-3 py-1 font-mono text-gray-800">{h}</td>
                            <td className="px-3 py-1 text-gray-500">{uploadedSchema.types[h] || 'string'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Table Name</label>
                <input
                  type="text"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  placeholder="e.g. customers"
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="byod-table-name-input"
                />
                {tableName && (
                  <p className="mt-0.5 text-xs text-gray-500">
                    Qualified name: <code>{dataSource.table_prefix}__{tableName}</code>
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSubmitTableName}
                  disabled={!tableName.trim()}
                  className="px-4 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="byod-write-submit-btn"
                >
                  Save as Table
                </button>
                <button
                  onClick={cancelUpload}
                  className="px-4 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {polling && !workflowStatus?.status?.match(/^(completed|failed|timeout)$/) && (
            <button
              onClick={cancelUpload}
              className="mt-2 px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {/* FileUploadModal (step 1): opens immediately on "Upload File" click */}
      {showUploadModal && (
        <FileUploadModal
          onClose={() => { setShowUploadModal(false); if (!uploadedFileId) cancelUpload(); }}
          onUploadComplete={(response) => {
            handleUploadComplete(response);
          }}
        />
      )}

      {/* Table list */}
      {loading ? (
        <div className="text-sm text-gray-500">Loading tables...</div>
      ) : error ? (
        <div className="text-sm text-red-600" data-testid="byod-error">{error}</div>
      ) : tables.length === 0 ? (
        <div className="text-sm text-gray-500 py-4 text-center" data-testid="byod-empty-state">
          No tables yet. Upload a file to create your first table.
        </div>
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg" data-testid="byod-tables-list">
          {renameError && (
            <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-sm text-red-700" data-testid="byod-rename-error">
              {renameError}
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Table Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rows</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Columns</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tables.map((t) => (
                <tr key={t.table_name} data-testid={`byod-table-row-${t.table_name}`}>
                  <td className="px-4 py-2 text-sm font-mono text-gray-900">
                    {renaming?.tableName === t.table_name ? (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-400">{dataSource.table_prefix}__</span>
                        <input
                          type="text"
                          value={renaming.value}
                          onChange={(e) => setRenaming((prev) => ({ ...prev, value: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameSubmit();
                            if (e.key === 'Escape') setRenaming(null);
                          }}
                          autoFocus
                          className="border border-blue-400 rounded px-2 py-0.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                          data-testid={`rename-input-${t.table_name}`}
                        />
                        <button
                          onClick={handleRenameSubmit}
                          className="text-xs text-blue-600 hover:text-blue-800 px-1"
                          data-testid={`rename-confirm-${t.table_name}`}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setRenaming(null)}
                          className="text-xs text-gray-500 hover:text-gray-700 px-1"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      `${dataSource.table_prefix}__${t.table_name}`
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">{t.filename || '-'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{t.row_count ?? '-'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{t.column_count ?? '-'}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">
                    {t.updated_at ? new Date(t.updated_at).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-3">
                      {renaming?.tableName !== t.table_name && (
                        <button
                          onClick={() => handleRenameStart(t.table_name)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                          data-testid={`rename-table-btn-${t.table_name}`}
                        >
                          Rename
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(t.table_name)}
                        className="text-xs text-red-600 hover:text-red-800"
                        data-testid={`delete-table-btn-${t.table_name}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BYODSourceDetail;
